import React, { useState, useEffect } from "react";
import SLBScreen from "../assets/portfolio/SLBScreen.JPG";
import naras from "../assets/portfolio/naras.JPG";
import NGO from "../assets/portfolio/NGO.JPG";
import Supercell from "../assets/portfolio/supercell.JPG";
import merkebmockup from "../assets/portfolio/merkebmockup.JPG";
import kaleabzelaem from "../assets/portfolio/kaleabzelalem.jpg";

const Portfolio = () => {
  const [selectedProject, setSelectedProject] = useState(null);

  // Disable background scrolling when modal is open
  useEffect(() => {
    if (selectedProject) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [selectedProject]);

  const portfolios = [
    {
      id: 1,
      src: SLBScreen,
      title: "Corporate Website – Coffee Export Company, Ethiopia",
      caseStudy: {
        overview:
          "The client had no digital presence, making it difficult to reach international buyers and showcase their coffee products effectively.",
        problem:
          "Without a website, the company struggled to connect with global customers and communicate product offerings, limiting potential sales.",
        goals: {
          primary: [
            "Establish a professional online presence",
            "Highlight coffee products clearly",
            "Attract and convert international buyers"
          ],
          secondary: [
            "Ensure responsive design across devices",
            "Maintain accessibility standards"
          ]
        },
        userFlows: {
          browsing: "Homepage → Product pages → Contact form",
        },
        myRole: [
          "UI/UX design",
          "Wireframing and prototyping",
          "High-fidelity screens in Figma"
        ],
        frontend: "Front-end implementation handled by the client's development team.",
        outcome:
          "The designed website provided a clear digital presence and helped connect the company with international buyers."
      },
      url: "https://slbtradingplc.com/",
    },
    {
      id: 2,
      src: naras,
      title: "Handcrafted Jewelry Brand Website",
      caseStudy: {
        overview:
          "The brand wanted a refined digital presence that reflects the craftsmanship and elegance of its handmade jewelry.",
        problem:
          "The jewelry brand had no platform to properly showcase its products online, limiting visibility and customer trust.",
        goals: {
          primary: [
            "Present jewelry products in a clean, elegant layout",
            "Encourage exploration and customer inquiries"
          ],
          secondary: [
            "Maintain responsive design across devices",
            "Keep the interface visually minimal and distraction-free"
          ]
        },
        userFlows: {
          browsing: "Landing Page → Product Details → Contact Form"
        },
        myRole: [
          "UI/UX design from wireframes to high-fidelity mockups",
          "Visual hierarchy and layout optimization",
          "Responsive and accessibility-focused design"
        ],
        frontend: "Prepared design assets and guidelines for the development team.",
        outcome:
          "The design allows users to explore products effortlessly and positions the brand as elegant and trustworthy."
      },
      url: "https://www.behance.net/gallery/218441523/Landing-page-for-handmade-jewelry"
    },
    {
      id: 3,
      src: NGO,
      title: "Full-Scale Website - NGO",
      caseStudy: {
        overview:
          "A responsive landing page designed to communicate the NGO’s mission clearly and encourage donations.",
        problem:
          "The NGO lacked a centralized platform to explain its mission or accept donations efficiently.",
        goals: {
          primary: [
            "Increase donor engagement",
            "Clearly communicate the NGO’s mission"
          ],
          secondary: [
            "Ensure accessibility and usability",
            "Provide a responsive experience across devices"
          ],
        },
        userFlows: {
          donation: "Landing Page → Mission Overview → Donation Form",
        },
        myRole: [
          "UI/UX design and wireframing",
          "Donation flow optimization",
          "Design collaboration with developers"
        ],
        frontend: "Prepared design assets and specifications for the development team.",
        outcome:
          "The landing page simplifies donations and improves clarity, resulting in better donor engagement."
      },
      url: "https://www.behance.net/gallery/215897297/Landing-Page-NGO",
    },
    {
      id: 4,
      src: Supercell,
      title: "Corporate Website — Event Internet Service Provider, NewYork",
      caseStudy: {
        overview:
          "A redesign for an internet service provider focused on event connectivity and reliability.",
        problem:
          "The outdated website failed to communicate services clearly and discouraged inquiries.",
        goals: {
          primary: ["Increase qualified inquiries"],
          secondary: [
            "Improve usability",
            "Clearly highlight services",
            "Maintain responsiveness"
          ],
        },
        userFlows: {
          inquiry: "Homepage → Services → Contact → Submit Inquiry",
        },
        myRole: [
          "UI/UX design",
          "Wireframing",
          "Information architecture"
        ],
        frontend: "Front-end implementation handled by client’s development team.",
        outcome:
          "The redesigned site improves clarity and streamlines the inquiry process."
      },
      url: "https://www.figma.com/proto/ltHVQpv962pc4hWEXFmqns/Untitled",
    },
    {
      id: 5,
      src: merkebmockup,
      title: "Merkeb Visuals — Photography Website",
      caseStudy: {
        overview:
          "A photography platform built to showcase work, enable bookings, and sell prints.",
        problem:
          "Users lacked a streamlined way to browse work or book sessions.",
        goals: {
          primary: ["Increase photoshoot bookings"],
          secondary: [
            "Enable print purchases",
            "Reduce friction between discovery and action"
          ]
        },
        userFlows: {
          booking: "Landing → Gallery → Service → Booking",
          purchase: "Gallery → Image → Print → Purchase"
        },
        myRole: [
          "UI/UX design",
          "Frontend development",
          "Interaction design"
        ],
        frontend:
          "Frontend built by me using Next.js, Tailwind CSS, and Framer Motion.",
        outcome:
          "A clean, mobile-optimized platform that converts traffic into bookings."
      },
      url: "https://merkebvisuals.vercel.app/",
    },
    {
      id: 6,
      src: kaleabzelaem,
      title: "Project Manager — Personal Portfolio",
      caseStudy: {
        overview:
          "A personal website designed to establish credibility and attract corporate clients.",
        problem:
          "The client had no digital presence to communicate experience or services.",
        goals: {
          primary: [
            "Clearly present experience and services",
            "Enable easy client contact"
          ],
          secondary: [
            "Maintain a professional tone",
            "Ensure responsive design"
          ]
        },
        userFlows: {
          contact: "Landing → Services → Contact",
        },
        myRole: [
          "UI/UX design",
          "Content structuring",
          "Frontend development"
        ],
        frontend:
          "Built using Next.js and Tailwind CSS for speed and responsiveness.",
        outcome:
          "The site establishes credibility and simplifies client outreach."
      },
      url: "https://kaleabzelalem.vercel.app/"
    }
  ];

  return (
    <div
      name="portfolio"
      className="w-full min-h-screen  py-12 bg-gradient-to-r from-sky-950 via-slate-800 to-slate-900"
    >
      <div className="max-w-screen-lg px-9 md:py-5 mx-auto">
        <h2 className="text-4xl font-bold text-white border-b-4 inline-block mb-6">
          Portfolio
        </h2>

        {/* GRID */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
          {portfolios.map((project) => (
            <div
              key={project.id}
              className="shadow-md shadow-black rounded-lg overflow-hidden"
            >
              <img
                src={project.src}
                alt={project.title}
                className="w-full h-48 object-cover hover:scale-105 duration-200"
              />
              <div className="p-4">
                <h3 className="text-white font-semibold">{project.title}</h3>
                <button
                  onClick={() => setSelectedProject(project)}
                  className="text-gray-400 hover:text-white mt-2 text-sm"
                >
                  Know more
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* MODAL */}
        {selectedProject && (
          <div
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4"
            onClick={() => setSelectedProject(null)}
          >
            <div
              className="bg-gradient-to-r from-sky-950 via-slate-800 to-slate-900 text-white rounded-lg max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6 relative
                         scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-transparent"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute top-3 right-3 text-gray-300 hover:text-white"
                onClick={() => setSelectedProject(null)}
              >
                ✖
              </button>

              <h2 className="text-2xl font-bold mb-4">{selectedProject.title}</h2>

              <img
                src={selectedProject.src}
                alt={selectedProject.title}
                className="rounded-md mb-4"
              />

              {/* Case Study */}
              <div className="space-y-4">
                <h3 className="text-xl font-semibold">Overview</h3>
                <p>{selectedProject.caseStudy.overview}</p>

                <h3 className="text-xl font-semibold">Problem</h3>
                <p>{selectedProject.caseStudy.problem}</p>

                <h3 className="text-xl font-semibold">Goals</h3>

                <h4 className="text-lg font-semibold">Primary Goals</h4>
                <ul className="list-disc ml-6">
                  {selectedProject.caseStudy.goals.primary.map((g, i) => (
                    <li key={i}>{g}</li>
                  ))}
                </ul>

                <h4 className="text-lg font-semibold">Secondary Goals</h4>
                <ul className="list-disc ml-6">
                  {selectedProject.caseStudy.goals.secondary.map((g, i) => (
                    <li key={i}>{g}</li>
                  ))}
                </ul>

                <h3 className="text-xl font-semibold">Outcome</h3>
                <p>{selectedProject.caseStudy.outcome}</p>
              </div>

              <a
                href={selectedProject.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-6 text-blue-400 hover:underline"
              >
                View Live Project →
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Portfolio;
